/**
 * Fetch with Timeout Utility
 *
 * Wraps the native fetch API with timeout support to prevent
 * hanging requests to external services.
 *
 * @example
 * import { fetchWithTimeout } from '@/utils/http/fetchWithTimeout';
 *
 * const response = await fetchWithTimeout(url, options, 10000);
 */

/**
 * Fetch with timeout
 * @param url - URL to fetch
 * @param options - Fetch options (method, headers, body, credentials, etc.)
 * @param timeoutMs - Timeout in milliseconds (default 30 seconds)
 * @returns Fetch response
 * @throws Error if request times out or fails
 */
export async function fetchWithTimeout(
  url: string,
  options: RequestInit = {},
  timeoutMs: number = 30000
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    return response;
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error(`Request timeout after ${timeoutMs}ms: ${url}`);
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

export default fetchWithTimeout;
